import { useEffect, useRef, useState } from "react";
import { BiPlus, BiSave } from "react-icons/bi";
import { MdCheck, MdClose, MdFilterList } from "react-icons/md";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import FilterRow from "./FilterRow";
import {
  FIELD_TYPES,
  getAvailableFields,
  getFieldConfig,
  NO_VALUE_OPERATORS,
} from "./config";

const AdvancedFilterPanel = ({
  fields,
  panelFilters,
  setPanelFilters,
  appliedFilters,
  onApply,
  onSave,
  context,
  translate,
  selectionOptions,
  filterCount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const panelRef = useRef(null);
  const saveInputRef = useRef(null);

  const availableFields = getAvailableFields(fields, context);
  const usedFieldKeys = panelFilters.map((f) => f.fieldKey).filter(Boolean);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!panelRef.current) return;

      const target = e.target;
      if (
        target.closest(".ant-select-dropdown") ||
        target.closest(".ant-picker-dropdown")
      ) {
        return;
      }

      if (!panelRef.current.contains(target)) {
        setIsOpen(false);
        setIsSaving(false);
        setSaveName("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isSaving && saveInputRef.current) {
      saveInputRef.current.focus();
    }
  }, [isSaving]);

  const togglePanel = () => {
    if (!isOpen) {
      const initial =
        appliedFilters.length > 0
          ? appliedFilters.map((f) => ({ ...f }))
          : [{}];
      setPanelFilters(initial);
    }
    setIsOpen(!isOpen);
    setIsSaving(false);
    setSaveName("");
  };

  const addFilterRow = () => {
    setPanelFilters((prev) => [...prev, {}]);
  };

  const updateFilter = (index, updated) => {
    setPanelFilters((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...updated } : item)),
    );
  };

  const removeFilter = (index) => {
    setPanelFilters((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length === 0 ? [{}] : updated;
    });
  };

  const isFilterComplete = (f) => {
    if (!f.fieldKey || !f.operator) return false;
    if (NO_VALUE_OPERATORS.includes(f.operator)) return true;
    if (f.operator === "between")
      return !!(f.datePresetKey || (f.value && f.value2));
    if (f.operator === "is" || f.operator === "is_not") {
      const cfg = getFieldConfig(f.fieldKey, fields);
      if (cfg?.type === FIELD_TYPES.DATE) {
        return !!(f.datePresetKey || f.value);
      }
      return f.values && f.values.length > 0;
    }
    return !!f.value;
  };

  const completeFilters = panelFilters.filter(isFilterComplete);
  const canApply = completeFilters.length > 0;
  const canSave = canApply;
  const canAddMore = usedFieldKeys.length < availableFields.length;

  const handleApply = () => {
    onApply(completeFilters);
    setIsOpen(false);
    setIsSaving(false);
    setSaveName("");
  };

  const handleStartSave = () => {
    setIsSaving(true);
  };

  const handleCancelSave = () => {
    setIsSaving(false);
    setSaveName("");
  };

  const handleConfirmSave = () => {
    if (!saveName.trim() || !canSave) return;
    onSave(saveName.trim(), completeFilters);
    setIsOpen(false);
    setIsSaving(false);
    setSaveName("");
  };

  const handleSaveKeyDown = (e) => {
    if (e.key === "Enter") handleConfirmSave();
    if (e.key === "Escape") handleCancelSave();
  };

  return (
    <div className="advanced-filter__container" ref={panelRef}>
      <Button
        type={filterCount > 0 ? "primary" : "primary--outlined"}
        onClick={togglePanel}
        alignment="right"
        className="advanced-filter__trigger"
      >
        <MdFilterList size={16} />
        <span>
          {translate.TITLE}
          {filterCount > 0 && ` (${filterCount})`}
        </span>
        {isOpen ? <RiArrowUpSLine size={18} /> : <RiArrowDownSLine size={18} />}
      </Button>

      {isOpen && (
        <div className="advanced-filter__panel">
          <div className="advanced-filter__rows">
            {panelFilters.map((filter, index) => (
              <FilterRow
                key={`filter-row-${index}`}
                filter={filter}
                index={index}
                availableFields={availableFields}
                usedFieldKeys={usedFieldKeys}
                onUpdate={updateFilter}
                onRemove={removeFilter}
                translate={translate}
                selectionOptions={selectionOptions}
                fields={fields}
              />
            ))}
          </div>

          {canAddMore && (
            <Button
              type="primary--link"
              onClick={addFilterRow}
              className="advanced-filter__add-btn"
            >
              <BiPlus size={16} />
              {translate.ADD_FILTER}
            </Button>
          )}

          <div className="advanced-filter__footer">
            {!isSaving ? (
              <Button
                type="gray--outlined"
                onClick={handleStartSave}
                disabled={!canSave}
              >
                <BiSave size={16} />
                {translate.SAVE_FILTERS}
              </Button>
            ) : (
              <div className="advanced-filter__save-inline">
                <Input
                  ref={saveInputRef}
                  placeholder={translate.SAVE_NAME_PLACEHOLDER}
                  value={saveName}
                  onChange={({ target: { value } }) => setSaveName(value)}
                  onKeyDown={handleSaveKeyDown}
                />
                <Button
                  type="primary"
                  onClick={handleConfirmSave}
                  disabled={!saveName.trim()}
                >
                  <MdCheck size={16} />
                </Button>
                <Button type="gray--outlined" onClick={handleCancelSave}>
                  <MdClose size={16} />
                </Button>
              </div>
            )}
            <Button type="primary" onClick={handleApply} disabled={!canApply}>
              {translate.APPLY_FILTERS}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilterPanel;
